import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, of, take } from "rxjs";
import { BannerCardDismissService } from "@modules/shared/banner-card-module/banner-card-dismiss.service";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { TaxesStateService } from "@modules/taxes/domain/+state/taxes.state.service";
import {
	IBCITaxCreateRequest,
	ITinVerificationStatusResponse,
	ITinVerificationWidgetStatusResponse,
	TaxesRemote
} from "@modules/taxes/domain/infrastructure/taxes.remote";
import {
	ITaxInOffer,
	SelfCertificationModel,
	TaxesRouteNames,
	TaxesUserStatusModel,
	ToastMessages
} from "@modules/taxes/domain/entities/taxes.model";
import { TaxesNavigationService } from "@modules/taxes/domain/infrastructure/taxes-navigation.service";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { ModalService } from "@core-services/modal.service";
import { ToastrService } from "ngx-toastr";
import { TranslationService } from "@core-services/translation.service";
import { TaxesQueryParamName, TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import { Helper } from "@helpers/functions";
import { TAXES_NOT_ELIGIBLE_COOKIE_KEY } from "@helpers/constants";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { InfoService } from "@core-services/info.service";
import { ErrorClass } from "bp-common-models-lib";

interface BannerHomeVM {
	showBanner: boolean;
	isFirstStep: boolean;
	isUpdateInProgress: boolean;
}

interface HistoryBannerVM {
	showBanner: boolean;
	declarationsOpen: number;
}

interface IUpdateTaxStatusParamObject {
	status: boolean;
	queryNameTo?: TaxesRouteNames;
	walletId?: string;
	showToast?: boolean;
}

@Injectable({
	providedIn: "root"
})
export class TaxesFacade {
	private readonly navigationService = inject(TaxesNavigationService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly dismissService = inject(BannerCardDismissService);
	private readonly translationService = inject(TranslationService);
	private readonly modalService = inject(ModalService);
	private readonly taxesRemote = inject(TaxesRemote);
	private readonly state = inject(TaxesStateService);
	private readonly toastr = inject(ToastrService);
	private readonly statsigService = inject(StatsigClientService);
	private readonly infoService = inject(InfoService);

	public readonly isTaxesActive$ = this.launchDarklyService.isActive$(LD_FLAG.TAX_WITHHOLDING);
	public readonly isTaxesForTradeActive$ = this.launchDarklyService.isActive$(LD_FLAG.TAX_WITHHOLDING_TRADE);
	public readonly isTaxesTradeSummaryVariationActive$ = this.launchDarklyService.isActive$(
		LD_FLAG.TAX_WITHHOLDING_SUMMARY_VARIATION
	);
	private readonly isTinTaxEnabled$ = this.statsigService.isFeatureActive(FEAT_GATES.TIN_TAX_ENABLED);

	public readonly taxesBannerKey = "taxes_crypto_declaration_banner";

	public readonly taxStatus$: Observable<string> = this.state.taxStatus$;
	public isUpdateStatusInProgress$: Observable<boolean> = this.state.isUpdatingStatus$;

	private TIMverficationStatusReloadHandler = new BehaviorSubject<void>(undefined);
	private TIMverficationStatusReloadTrigger$ = this.TIMverficationStatusReloadHandler.asObservable();

	public getBannerHomeVM$: Observable<BannerHomeVM> = combineLatest([
		this.isTaxesActive$,
		this.dismissService.getIsShowingForCurrentUser$(this.taxesBannerKey),
		this.state.declarationsOpen$,
		this.state.taxStatus$,
		this.state.isUpdatingStatus$
	]).pipe(
		map(([isActive, isBannerVisible, declarationsOpen, taxStatus, isLoadingStatus]) => {
			return {
				showBanner:
					isActive &&
					((isBannerVisible && declarationsOpen > 0 && taxStatus === SelfCertificationModel.Eligible) ||
						taxStatus === SelfCertificationModel.Unconfirmed),
				isFirstStep: taxStatus === SelfCertificationModel.Unconfirmed,
				isUpdateInProgress: isLoadingStatus
			};
		})
	);

	public getBannerHistoryVM$: Observable<HistoryBannerVM> = combineLatest([
		this.isTaxesActive$,
		this.state.taxStatus$,
		this.state.declarationsOpen$
	]).pipe(
		map(([isActive, taxStatus, declarationsOpen]) => {
			return {
				showBanner: isActive && declarationsOpen > 0 && taxStatus === SelfCertificationModel.Eligible,
				declarationsOpen: declarationsOpen
			};
		})
	);

	public fetchTaxStatusForTrade$(walletId: string): Observable<TaxesUserStatusModel> {
		return this.taxesRemote.getUserTaxStatus$(walletId).pipe(
			tap((response) => {
				//side effect update cookie
				this.setCookieOnStatusUpdate(response);
			})
		);
	}

	public fetchUserTaxStatus(walletId?: string, forceUpdate: boolean = false): void {
		if (this.state.stateNotLoaded() || this.state.hasError() || forceUpdate) {
			this.taxesRemote.getUserTaxStatus$(walletId).subscribe({
				next: (response) => {
					this.state.updateState({
						hasError: false,
						isLoading: false,
						taxStatus: response.taxStatus,
						declarationsOpen: response.openDeclarationsCount
					});

					//update cookie
					this.setCookieOnStatusUpdate(response);
				},
				error: () => {
					this.state.taxesErrorState();
				}
			});
		}
	}

	private setCookieOnStatusUpdate(response: TaxesUserStatusModel): void {
		// If not eligible set a cookie if it doesn't already exist
		// This is to prevent the blocking of the sell flow is user-status endpoint fails and user is not eligible
		if (response.taxStatus === "NOT_ELIGIBLE") {
			Helper.setCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, "true", true);
		}
		// If eligible or unknown delete the cookie
		// Important to have this in case user updates the tax status the cookie is removed
		if (response.taxStatus !== "NOT_ELIGIBLE") {
			Helper.setCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, "false", true);
		}
	}

	public updateUserTaxStatus(updateStatusObject: IUpdateTaxStatusParamObject): void {
		const userStatus = updateStatusObject.status
			? SelfCertificationModel.Eligible
			: SelfCertificationModel.NonEligible;

		// Set the status to updating
		this.state.updateStatusInProgress(true);
		this.taxesRemote.updateUserTaxStatus$(updateStatusObject.status, updateStatusObject?.walletId).subscribe({
			next: () => {
				this.state.updateState({
					taxStatus: userStatus,
					isUpdatingStatus: false
				});
				this.onTaxStatusUpdate(
					updateStatusObject.status,
					updateStatusObject?.queryNameTo,
					updateStatusObject?.walletId
				);
				if (updateStatusObject.showToast) {
					this.toastr.success(this.translationService.translate(ToastMessages.Success));
				}
			},
			error: () => {
				this.state.updateStatusInProgress(false);
				this.navigationService.closeModal();
				this.toastr.error(this.translationService.translate(ToastMessages.Error));
			}
		});
	}

	private onTaxStatusUpdate(isUserEligible: boolean, queryNameTo: TaxesRouteNames, walletId?: string): void {
		const openDeclarations: boolean = walletId
			? this.state.hasDeclarationsOpenForWallet()
			: this.state.userHasDeclarationsOpen();
		const eligibleWithDeclarations: boolean = openDeclarations && isUserEligible;

		if (eligibleWithDeclarations) {
			if (queryNameTo) {
				this.navigationService.updateQueryParamsAndNavigate(queryNameTo);
			} else {
				this.navigationService
					.routeParamsReader()
					.pipe(take(1))
					.subscribe({
						next: async (query) => {
							const _query = query.fromTradeFlow ? query : null;
							this.navigationService.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclaration, _query);
						}
					});
			}
		} else if (queryNameTo) {
			this.navigationService.continueTradeFlow();
			this.navigationService.closeModal();
		} else {
			this.navigationService.closeModal();
		}
	}

	// logic to open the modal for the TW popup subject to status
	public openCryptoDeclarationTradeModal(
		queryParams: Partial<TaxesQueryParamsCollection>,
		walletStatus?: TaxesUserStatusModel
	): void {
		// From sell flow we make a new request to get the user status for a specific wallet but we don't want to update the state globally.
		if (walletStatus) {
			if (walletStatus.taxStatus === SelfCertificationModel.Eligible && walletStatus.openDeclarationsCount > 0) {
				this.state.updateState({
					taxStatus: SelfCertificationModel.Eligible,
					declarationsOpenForWallet: walletStatus.openDeclarationsCount
				});
				this.modalService.popAll();
				this.navigationService.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclarationTrade, queryParams);
			}
			if (walletStatus.taxStatus === SelfCertificationModel.Unconfirmed) {
				this.state.updateState({
					taxStatus: SelfCertificationModel.Unconfirmed,
					declarationsOpenForWallet: walletStatus.openDeclarationsCount
				});
				this.modalService.popAll();
				this.navigationService.gotoByNavigationUrl(TaxesRouteNames.SelfCertifyTrade, queryParams);
			}
			return;
		}
		this.fetchUserTaxStatus(queryParams[TaxesQueryParamName.AssetWalletId]);
		this.state.taxStatus$.subscribe({
			next: (status) => {
				if (status === SelfCertificationModel.Eligible && this.state.userHasDeclarationsOpen()) {
					this.modalService.popAll();
					this.navigationService.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclarationTrade, queryParams);
				}
				if (status === SelfCertificationModel.Unconfirmed) {
					this.modalService.popAll();
					this.navigationService.gotoByNavigationUrl(TaxesRouteNames.SelfCertifyTrade, queryParams);
				}
			}
		});
	}

	public goToCryptoDeclaration(): void {
		this.navigationService.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclaration);
	}

	/**
	 * get tax for BCI trade
	 * @param request - IBCITaxCreateRequest
	 * @returns a deserilaized ITaxWithheld object
	 */
	public fetchBCITaxCreate$(request: IBCITaxCreateRequest): Observable<ITaxInOffer> {
		return this.taxesRemote.createBCITaxGQL(request);
	}

	public getTinVerificationStatus$(): Observable<Partial<ITinVerificationStatusResponse>> {
		return this.isTinTaxEnabled$.pipe(
			switchMap((enabled) => {
				if (!enabled) {
					return of({ flagOn: false });
				}
				return this.taxesRemote.getTinVerificationStatus().pipe(
					map((response) => {
						return response && Object.keys(response).length > 0
							? { ...response, ...{ flagOn: true } }
							: null;
					}),
					catchError((error) => {
						if (error instanceof ErrorClass) {
							const errorMsg = this.translationService.translate("generic_error_title");
							this.infoService.setError(error?.responseErrors[0]?.title || errorMsg);
						}

						return of({ hasError: true });
					})
				);
			})
		);
	}

	public getWidgetTinVerificationStatus$(): Observable<Partial<ITinVerificationWidgetStatusResponse>> {
		return this.TIMverficationStatusReloadTrigger$.pipe(
			switchMap(() => this.isTinTaxEnabled$),
			switchMap((isEnabled) => {
				if (!isEnabled) {
					return of({ flagOn: false });
				}
				return this.taxesRemote.getTinVerificationWidgetStatus().pipe(
					map((response) => {
						return response && Object.keys(response).length > 0
							? { ...response, ...{ flagOn: true } }
							: null;
					}),
					catchError((error) => {
						if (error instanceof ErrorClass) {
							const errorMsg = this.translationService.translate("generic_error_title");
							this.infoService.setError(error?.responseErrors[0]?.title || errorMsg);
						}

						return of({ hasError: true });
					})
				);
			})
		);
	}

	public triggerTINVerificationStatusReload(): void {
		this.TIMverficationStatusReloadHandler.next();
	}
}
